import React from 'react';
import * as data from '../../data';
import {IAddress, IAddressCountry} from '../../i';

// The per-country state/region logic shared by the shipping form (both arms) and the redesign's billing form.

const countriesWithStates = ['us', 'ca', 'jp', 'gb', 'de'];

/** True when the address's country has a state/province list. True when the country cannot be read, so a broken address still gets the select. */
export function addressHasStates(addr: IAddress | undefined): boolean {
    try {
        return countriesWithStates.includes((addr as IAddress).country.value.toLowerCase());
    } catch {
        return true;
    }
}

interface IStateListEntry {
    id: number;
    name: string;
    value: string;
}

const stateListByCountry: Record<string, {cid: number; useDdl: boolean}> = {
    US: {cid: 217, useDdl: true},
    CA: {cid: 39, useDdl: false},
    DE: {cid: 78, useDdl: false},
    GB: {cid: 216, useDdl: false},
    JP: {cid: 104, useDdl: false},
};

/**
 * The entries of the state select for a country code, or null when the country has no list.
 * US keeps the data order ("Select State" first, then the Armed Forces entries) and uses the ddl codes, which
 * tell the Armed Forces regions apart (AE1..AE4); the other countries sort by name and use the plain code.
 */
export function stateList(country: string): IStateListEntry[] | null {
    const cfg = stateListByCountry[country];
    if (!cfg) {
        return null;
    }
    const states = data.states.filter(e => e.cid === cfg.cid);
    if (!cfg.useDdl) {
        states.sort((a, b) => a.name.localeCompare(b.name));
    }
    return states.map(e => ({id: e.id, name: e.name, value: cfg.useDdl ? e.ddl : e.code}));
}

/** The <option>s for the state select; `data-id` carries the data row id (the shipping form reads it for the Armed Forces company). */
export function stateOptions(country: string): React.ReactElement[] | null {
    const list = stateList(country);
    if (!list) {
        return null;
    }
    return list.map(e => (
        <option
            value={e.value}
            key={e.id}
            data-id={e.id}
        >
            {e.name}
        </option>
    ));
}

/** True when `code` is a country of the address list. */
export function isKnownCountryCode(code: string): boolean {
    return data.countries.some(c => c.code === code);
}

const firstCountryCodes = ['US', 'CA'];
let redesignCountries: IAddressCountry[] | null = null;

/**
 * The redesign's country list: United States and Canada first, the rest in data order. The data list starts with
 * Afghanistan, and an autofill (1Password) that cannot match its stored country against the options falls back to the
 * first one; with this order that fallback is the United States.
 */
export function redesignCountryList(): IAddressCountry[] {
    if (!redesignCountries) {
        const first = firstCountryCodes.map(code => data.countries.find(c => c.code === code)).filter((c): c is IAddressCountry => !!c);
        redesignCountries = [...first, ...data.countries.filter(c => !firstCountryCodes.includes(c.code))];
    }
    return redesignCountries;
}

/**
 * True when a change event left a select on no option at all (selectedIndex -1, value ''): an autofill set a value the
 * option list does not have. React would then show the first option while the store holds '', so the form keeps the
 * value it had instead.
 */
export function selectLostItsValue(select: Pick<HTMLSelectElement, 'selectedIndex' | 'value'>): boolean {
    return select.selectedIndex < 0 || select.value === '';
}

/**
 * The state value to keep when the country changes to `country`: the current one when the new country has no list
 * (except the US "Select State" placeholder, which becomes '') or its list has the value, else the list's first entry.
 */
export function stateValueForCountry(country: string, current: string): string {
    const list = stateList(country);
    if (!list) {
        return current === 'AL1' ? '' : current;
    }
    return list.some(e => e.value === current) ? current : list[0].value;
}
